import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, CheckCheck, AlertCircle, Bot, Play, Pause, Mic, Sparkles, Bug, Clock, FileText, Download, MapPin } from 'lucide-react';

export interface TranscriptMessage {
  id: string;
  role: 'inbound' | 'outbound' | 'system';
  body?: string;
  mediaType?: string;
  mediaUrl?: string;
  mediaFilename?: string;
  mediaStorageStatus?: string;
  flowResponse?: Record<string, unknown>;
  location?: { latitude?: number; longitude?: number; name?: string; address?: string };
  /** CDN URL of the audio file (set for voice notes) */
  audioUrl?: string;
  /** MIME type of the audio */
  audioMime?: string;
  /** Duration in seconds */
  audioDurationSec?: number;
  timestamp: string;
  /** If true, this outbound message was sent by the bot (actorKind === 'bot') */
  isBot?: boolean;
  /**
   * Distinguishes between scripted bot replies and conversational AI fallback
   * replies. 'whatsapp_ai_fallback' renders an "AI" badge instead of "Bot".
   */
  source?: string;
  /** Meta delivery state for outbound messages — drives the WhatsApp-style ticks. */
  deliveryStatus?: 'pending' | 'unknown' | 'dry_run' | 'sent' | 'delivered' | 'read' | 'failed';
  /** Set when deliveryStatus === 'failed' — shown as a tooltip on the error icon. */
  failureReason?: string;
  /** Template footer line (muted, under the body). */
  footer?: string;
  /** Template buttons — rendered as WhatsApp-style rows under the bubble text. */
  buttons?: { type: string; text?: string }[];
}

/** WhatsApp-style ticks: ✓ sent · ✓✓ delivered · blue ✓✓ read · red ! failed. */
const DeliveryTicks: React.FC<{ status?: TranscriptMessage['deliveryStatus']; failureReason?: string }> = ({ status, failureReason }) => {
  if (status === 'pending') return <span title="Waiting for WhatsApp"><Clock size={12} className="opacity-55" /></span>;
  if (status === 'unknown' || status === 'dry_run') return <span title={status === 'dry_run' ? 'Saved in CRM only — dry run' : failureReason}><AlertCircle size={12} className="text-warning" /></span>;
  if (status === 'failed') {
    return (
      <span title={failureReason ?? 'Delivery failed'}>
        <AlertCircle size={12} className="text-danger" />
      </span>
    );
  }
  if (status === 'read') return <CheckCheck size={12} className="text-sky-500" />;
  if (status === 'delivered') return <CheckCheck size={12} className="opacity-55" />;
  // 'sent' or legacy messages without a status callback recorded.
  return <Check size={12} className="opacity-55" />;
};

function formatDuration(sec?: number): string {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface AudioBubbleProps {
  audioUrl: string;
  transcript?: string;
  durationSec?: number;
  isOutbound: boolean;
}

const AudioBubble: React.FC<AudioBubbleProps> = ({ audioUrl, transcript, durationSec, isOutbound }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayPause = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.ontimeupdate = () => {
        if (!audioRef.current) return;
        const pct = audioRef.current.duration
          ? audioRef.current.currentTime / audioRef.current.duration
          : 0;
        setProgress(pct);
      };
      audioRef.current.onended = () => {
        setPlaying(false);
        setProgress(0);
      };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2.5">
        {/* Play / Pause circular button */}
        <button
          onClick={handlePlayPause}
          className={clsx(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition',
            isOutbound
              ? 'bg-primary text-white hover:bg-primary/80'
              : 'bg-surface-elevated text-text-primary hover:bg-surface-alt',
          )}
        >
          {playing
            ? <Pause size={13} fill="currentColor" />
            : <Play size={13} fill="currentColor" />}
        </button>

        {/* Waveform placeholder + progress */}
        <div className="relative flex-1 h-2 rounded-full bg-border overflow-hidden min-w-[80px]">
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all"
            style={{
              width: `${progress * 100}%`,
              background: isOutbound ? 'rgba(255,255,255,0.7)' : 'var(--color-primary)',
            }}
          />
          {/* Decorative waveform bars */}
          <div className="absolute inset-0 flex items-center gap-px pointer-events-none">
            {Array.from({ length: 18 }).map((_, i) => (
              <span
                key={i}
                className="flex-1 rounded-full opacity-25"
                style={{
                  height: `${30 + (i % 4) * 20}%`,
                  background: isOutbound ? 'white' : 'var(--color-primary)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Duration */}
        <div className="flex items-center gap-1 shrink-0">
          <Mic size={10} className="opacity-50" />
          <span className="font-mono text-[11px] opacity-70">{formatDuration(durationSec)}</span>
        </div>
      </div>

      {/* Transcript */}
      {transcript && (
        <p className="text-[12px] opacity-70 italic leading-snug">
          {transcript}
        </p>
      )}
    </div>
  );
};

function formatTime(ts: string) {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDate(ts: string) {
  try {
    const d = new Date(ts);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

interface Props {
  messages: TranscriptMessage[];
  loading?: boolean;
  /**
   * Set by the parent (admin only) to expose a "Debug" affordance on AI
   * messages. The transcript itself doesn't gate by role — that's the
   * caller's responsibility.
   */
  onDebugClick?: (leadEventUid: string) => void;
}

export const ConversationTranscript: React.FC<Props> = ({ messages, loading, onDebugClick }) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-3 px-4 py-4">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={clsx(
              'flex',
              i % 2 === 0 ? 'justify-start' : 'justify-end',
            )}
          >
            <div
              className={clsx(
                'skeleton h-10 rounded-2xl',
                i % 2 === 0 ? 'w-48' : 'w-36',
              )}
            />
          </div>
        ))}
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-text-muted">
        <span className="text-sm">No messages yet.</span>
      </div>
    );
  }

  // Group messages by date for date separators
  const groups: { date: string; messages: TranscriptMessage[] }[] = [];
  for (const msg of messages) {
    const date = formatDate(msg.timestamp);
    const last = groups[groups.length - 1];
    if (last && last.date === date) {
      last.messages.push(msg);
    } else {
      groups.push({ date, messages: [msg] });
    }
  }

  return (
    <div className="flex flex-col gap-1 px-4 py-4">
      {groups.map((group) => (
        <React.Fragment key={group.date}>
          {/* Date separator */}
          <div className="flex items-center justify-center py-2">
            <span className="rounded-md bg-surface-alt px-2.5 py-1 text-[10px] font-medium text-text-muted">
              {group.date}
            </span>
          </div>

          {group.messages.map((msg) => {
            if (msg.role === 'system') {
              return (
                <div key={msg.id} className="flex justify-center py-1">
                  <span className="rounded-lg bg-surface-alt px-3 py-1 text-[11px] text-text-muted italic">
                    {msg.body ?? 'System event'}
                  </span>
                </div>
              );
            }

            const isOutbound = msg.role === 'outbound';

            return (
              <div
                key={msg.id}
                className={clsx('flex flex-col', isOutbound ? 'items-end' : 'items-start')}
              >
                {/* Bot/AI indicator pill — shown above the bubble for bot/AI messages */}
                {isOutbound && msg.isBot && (
                  <div className="mb-0.5 flex items-center gap-1 px-1">
                    {(msg.source === 'whatsapp_ai_fallback' || msg.source === 'whatsapp_ai_bot') ? (
                      <>
                        <Sparkles size={10} className="text-violet-500 opacity-80" />
                        <span className="text-[10px] font-medium text-violet-500 opacity-80">AI</span>
                        {onDebugClick && (
                          <button
                            type="button"
                            onClick={() => onDebugClick(msg.id)}
                            className="ml-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-violet-500/80 hover:bg-violet-500/10 hover:text-violet-600"
                            title="Open AI debug snapshot for this reply"
                          >
                            <Bug size={10} />
                            Debug
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <Bot size={10} className="text-primary opacity-70" />
                        <span className="text-[10px] font-medium text-primary opacity-70">Bot</span>
                      </>
                    )}
                  </div>
                )}
                <div
                  className={clsx(
                    'relative max-w-[88%] sm:max-w-[76%] lg:max-w-[68%] rounded-2xl px-3.5 py-2.5 shadow-sm',
                    isOutbound && msg.isBot && (msg.source === 'whatsapp_ai_fallback' || msg.source === 'whatsapp_ai_bot')
                      ? 'rounded-br-sm bg-violet-50 dark:bg-violet-900/20 text-violet-900 dark:text-violet-200 border border-violet-200 dark:border-violet-800/40'
                      : isOutbound && msg.isBot
                      ? 'rounded-br-sm bg-primary-soft/60 text-primary border border-primary/20'
                      : isOutbound
                      ? 'rounded-br-sm bg-[#d9fdd3] dark:bg-emerald-900/40 text-text-primary'
                      : 'rounded-bl-sm bg-surface border border-border text-text-primary',
                  )}
                >
                  {/* Voice / audio message */}
                  {msg.audioUrl ? (
                    <AudioBubble
                      audioUrl={msg.audioUrl}
                      transcript={msg.body}
                      durationSec={msg.audioDurationSec}
                      isOutbound={isOutbound}
                    />
                  ) : (
                    <>
                      {/* Image headers render inline like WhatsApp; other media keeps the compact chip */}
                      {(msg.mediaType === 'image' || msg.mediaType === 'sticker') && msg.mediaUrl ? (
                        <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="block">
                          <img src={msg.mediaUrl} alt="" className="mb-1.5 max-h-56 w-full rounded-lg object-cover" />
                        </a>
                      ) : msg.mediaType === 'video' && msg.mediaUrl ? (
                        <video controls preload="metadata" className="mb-2 max-h-72 max-w-full rounded-lg" src={msg.mediaUrl} />
                      ) : msg.mediaType === 'document' && msg.mediaUrl ? (
                        <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="mb-2 flex items-center gap-3 rounded-xl border border-current/10 bg-surface/60 p-3"><FileText size={26} className="shrink-0 text-primary" /><span className="max-w-48 truncate text-sm">{msg.mediaFilename || 'Document'}</span><Download size={15} className="shrink-0 opacity-60" /></a>
                      ) : msg.location && Number.isFinite(msg.location.latitude) && Number.isFinite(msg.location.longitude) ? (
                        <a href={`https://www.google.com/maps?q=${msg.location.latitude},${msg.location.longitude}`} target="_blank" rel="noreferrer" className="mb-2 flex items-center gap-3 rounded-xl bg-surface/70 p-4"><MapPin size={26} className="text-success" /><span className="text-sm">{msg.location.name || 'Shared location'}<span className="block text-xs opacity-60">{msg.location.address || 'Open in maps'}</span></span></a>
                      ) : msg.mediaType && msg.mediaType !== 'text' && (
                        <div className="mb-1.5 rounded-lg bg-surface-alt px-2 py-1 text-[11px] text-text-muted">
                          {msg.mediaStorageStatus === 'pending' ? 'Saving attachment — automatic retry pending' : `[${msg.mediaType}`}
                          {msg.mediaUrl && (
                            <a
                              href={msg.mediaUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-1 underline"
                            >
                              view
                            </a>
                          )}
                          {msg.mediaStorageStatus !== 'pending' && ']'}
                        </div>
                      )}
                      {msg.body && (
                        <p className="text-[13.5px] leading-snug whitespace-pre-wrap break-words">
                          {msg.body}
                        </p>
                      )}
                      {msg.flowResponse && <dl className="mt-3 space-y-2 rounded-xl border border-current/10 bg-surface/60 p-3">{Object.entries(msg.flowResponse).filter(([key]) => key !== 'flow_token').map(([key, value]) => <div key={key}><dt className="text-[10px] uppercase tracking-wide opacity-60">{key.replace(/_/g, ' ')}</dt><dd className="mt-0.5 break-words text-sm">{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}</dd></div>)}</dl>}
                      {msg.footer && (
                        <p className="mt-1 text-[11px] leading-snug opacity-60">{msg.footer}</p>
                      )}
                      {(msg.buttons ?? []).length > 0 && (
                        <div className="mt-1.5 space-y-1 border-t border-current/10 pt-1.5">
                          {msg.buttons!.map((b, bi) => (
                            <div key={bi} className="rounded-md bg-surface/60 py-1 text-center text-[12px] font-medium">
                              {b.type === 'COPY_CODE' ? 'Copy code' : (b.text || b.type)}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {msg.deliveryStatus === 'failed' && <p className="mt-2 max-w-sm text-xs text-danger">{msg.failureReason || 'Message could not be delivered'}</p>}
                  <div
                    className={clsx(
                      'mt-1 flex items-center gap-1',
                      isOutbound ? 'justify-end' : 'justify-start',
                    )}
                  >
                    <span className="text-[10px] opacity-55">{formatTime(msg.timestamp)}</span>
                    {isOutbound && (
                      <DeliveryTicks status={msg.deliveryStatus} failureReason={msg.failureReason} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};
